import NextApp from "next/app";
import type { AppContext, AppProps } from "next/app";
import type { NextComponentType } from "next";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { appWithTranslation } from "next-i18next/pages";
import nextI18NextConfig from "../next-i18next.config";
import { Provider } from "urql";
import { ErrorBoundary } from "@appsignal/react";
import "react-datasheet-grid/dist/style.css";
import { useRouter } from "next/router";
import { LicenseInfo } from "@mui/x-license";

// custom
import { urqlClient } from "lib/graphqlClient";
import { ReleaseGuardProvider } from "lib/release/ReleaseGuardProvider";
import { PermissionDeniedNoticeHost } from "lib/permission/PermissionDeniedNoticeHost";
import { MeFieldFragment } from "graphql/generated";
import { MeContext } from "lib/auth";
import { DictContext as _DictContext } from "lib/dict";
import { DictProvider } from "lib/dictProvider";
import { AnalyticsTags, usePageView } from "lib/AnalyticsTags";
import "../styles/globals.css";
import { appsignal, appsignalTags } from "lib/appsignal";
import theme from "lib/theme";
import { LoadingProvider, Loading } from "lib/loadingContext";
import { MuiDataGridEditingProvider } from "lib/muiDataGridEditingContext";
import JotaiProvider from "lib/jotaiProvider";
import { usePageTransition } from "lib/usePageTransition";
import GlobalMenuProvider from "lib/globalMenuProvider";
import { parseAcceptLanguage } from "lib/parseAcceptLanguage";
import { GoogleMapsApiKeyProvider } from "lib/googleMapsApiKeyProvider";
import PageHead from "components/atoms/PageHead";
import Favicon from "components/atoms/Favicon";

// mui
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createEmotionCache from "lib/createEmotionCache";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

type DictContentType = Record<string, unknown>;

// MUI premiumのライセンスキーを設定
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_PREMIUM_LICENSE_KEY || "no-license-key");

const GlobalHeader = dynamic(() => import("components/GlobalHeader"), { ssr: false });

// MUI premiumのライセンスキーを設定
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_PREMIUM_LICENSE_KEY || "no-license-key");

const FallbackComponent = () => <div>Sorry... There was something wrong. Please reload or contact to administrator.</div>;

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  pageProps: {
    me: MeFieldFragment;
    dict: DictContentType;
    dictNameSpaces: string[];
    layout: string | null;
    primaryLang: string;
  };
}

// 画面遷移の読み込み中の表示。App の描画のたびに作り直さないよう外に置く
const PageLoading: React.FC<{ open: boolean }> = ({ open }) => (
  <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }} open={open}>
    <CircularProgress color="inherit" />
  </Backdrop>
);

const App: NextComponentType<AppContext, any, MyAppProps> = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  usePageView(); // GA用のイベントフックを仕込む。
  const router = useRouter();

  // メンテナンスページへの強制リダイレクト
  // useEffect(() => {
  //   if (router.pathname !== "/maintenance" && router.pathname !== "/404" && router.pathname !== "/_error") {
  //     router.replace("/maintenance");
  //   }
  // }, [router]);

  // ユーザーの言語設定に応じてhtml lang属性を設定
  // ブラウザの翻訳機能が正しく動作するようにするため
  useEffect(() => {
    const lang = pageProps.me?.languageCode || "ja";
    document.documentElement.lang = lang;
  }, [pageProps.me?.languageCode]);

  // https://github.com/vercel/next.js/blob/canary/examples/with-loading/pages/_app.tsx
  // を参考に。画面遷移時にLoadingするようにするための処理。
  // shallow な遷移（同じ画面の中でクエリだけ変える）では読み込み中を出さず、jotai の store も作り直さない
  const { pageLoading, storeKey } = usePageTransition(router.events, router.asPath);

  //エラーの時はGlobalHeader諸々スキップ
  if (router.pathname == "/404" || router.pathname == "/_error") return <Component {...pageProps} />;

  // NOTE: JotaiProviderとGlobalMenuProviderがともにjotaiのProviderになっている。前者はキー（usePageTransition）を持っていて画面遷移のたびに構築され直すが（shallow な遷移では構築し直さない）、後者はあえてふっていない(GlobalMenuの開閉状態は常に保持しておきたいので)
  //       なので、ここの間に新たにjotaiに依存するatomを定義する場合には上記のことに気をつけてください。
  return (
    <ErrorBoundary instance={appsignal} tags={appsignalTags(pageProps.me, router.asPath)} fallback={() => <FallbackComponent />}>
      <Provider value={urqlClient}>
        <GoogleMapsApiKeyProvider>
          <CacheProvider value={emotionCache}>
            <ThemeProvider theme={theme}>
              <MeContext.Provider value={pageProps.me}>
                <DictProvider
                  dictFromPage={pageProps.dict}
                  debug={router.query.debug_dict != undefined}
                  namespacesSpecifiedInPage={pageProps.dictNameSpaces}
                >
                  {/* 公開版の確認と更新案内（lib/release/ReleaseGuardProvider.tsx）。案内の文言は dict の common を使う */}
                  <ReleaseGuardProvider>
                    {/* API がロールの権限不足で断ったときの通知（lib/permission/PermissionDeniedNoticeHost.tsx）。文言は dict の common を使う */}
                    <PermissionDeniedNoticeHost />
                    <GlobalMenuProvider>
                      <CssBaseline />
                      {/* GlobalHeaderはGlobalMenuProviderの直下（JotaiProviderの外） */}
                      {pageProps.layout === "none" ? (
                        <JotaiProvider storeKey={storeKey}>
                          <LoadingProvider>
                            <MuiDataGridEditingProvider>
                              <PageHead primaryLang={pageProps.primaryLang} />
                              <Favicon />
                              <AnalyticsTags />
                              <Loading />
                              <Component {...pageProps} />
                            </MuiDataGridEditingProvider>
                          </LoadingProvider>
                        </JotaiProvider>
                      ) : pageProps.layout === "noLoginHeader" ? (
                        <GlobalHeader noMenu>
                          <JotaiProvider storeKey={storeKey}>
                            <LoadingProvider>
                              <MuiDataGridEditingProvider>
                                <PageHead primaryLang={pageProps.primaryLang} />
                                <Favicon />
                                <AnalyticsTags />
                                <PageLoading open={pageLoading} />
                                <Loading />
                                <Component {...pageProps} />
                              </MuiDataGridEditingProvider>
                            </LoadingProvider>
                          </JotaiProvider>
                        </GlobalHeader>
                      ) : (
                        <GlobalHeader>
                          <JotaiProvider storeKey={storeKey}>
                            <LoadingProvider>
                              <MuiDataGridEditingProvider>
                                <PageHead primaryLang={pageProps.primaryLang} />
                                <Favicon />
                                <AnalyticsTags />
                                <PageLoading open={pageLoading} />
                                <Loading />
                                <Component {...pageProps} />
                              </MuiDataGridEditingProvider>
                            </LoadingProvider>
                          </JotaiProvider>
                        </GlobalHeader>
                      )}
                    </GlobalMenuProvider>
                  </ReleaseGuardProvider>
                </DictProvider>
              </MeContext.Provider>
            </ThemeProvider>
          </CacheProvider>
        </GoogleMapsApiKeyProvider>
      </Provider>
    </ErrorBoundary>
  );
};

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext);

  // DBのロケールではなく、HTTPのAccept-Languageリクエストヘッダーのロケールを取得します。
  const acceptLang = appContext.ctx.req?.headers["accept-language"];
  const primaryLang = parseAcceptLanguage(acceptLang);

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      primaryLang: primaryLang,
    },
  };
};

export default appWithTranslation(App as React.FC, nextI18NextConfig);
